import * as Fields from "@areahub/ui/application/forms/src/common";
import {
  FormInputProps,
  FormSelectProps,
  FormTextAreaProps,
} from "@areahub/ui/application/forms/src/common";
import {
  Box,
  BoxProps,
  Button,
  Checkbox,
  Flex,
  Heading,
  Stack,
  Text,
} from "@chakra-ui/react";
import { RichText } from "@graphcms/rich-text-react-renderer";
import { richTextRenderers } from "components/individuals/mdx-components";
import { GcmsFormFragment } from "lib/gcms/fragment-typings";
import { BaseSyntheticEvent, useState } from "react";
import {
  FieldValues,
  useForm,
  UseFormReset,
  UseFormSetError,
} from "react-hook-form";

export type DynamicFormSubmitFunction = (
  values: any,
  setSuccess: (value: boolean) => void,
  setError: UseFormSetError<FieldValues>,
  reset: UseFormReset<FieldValues>,
  event?: BaseSyntheticEvent<object, any, any>,
) => void;
export interface DynamicFormProps
  extends Omit<GcmsFormFragment, "color" | "bg" | "successMessage"> {
  color?: string;
  bg?: string;
  sx?: BoxProps;
  onClose: () => void;
  successMessage?: any;
  onSubmit: DynamicFormSubmitFunction;
  showError: boolean;
  newsletterText?: string;
  from?: string;
  slug?: string;
}

export default function DynamicForm({
  title,
  bg,
  color,
  fields,
  submitButton,
  successMessage,
  onClose,
  sx,
  onSubmit,
  showError = true,
  slug,
  from,
  newsletterText,
}: DynamicFormProps) {
  const {
    handleSubmit,
    register,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm();
  const [success, setSuccess] = useState<any>(null);
  const [newsletter, setNewsletter] = useState(false);
  if (!fields) return null;
  return (
    <Box
      bg={bg}
      color={color}
      maxW={{ base: "xl", md: "3xl" }}
      px={{ base: "6", md: "8" }}
      py="12"
      borderRadius={{
        base: "1.75rem 1.75rem 0px 0px",
        md: "md",
      }}
      mt="0px !important"
      {...sx}
    >
      <Heading
        as="h1"
        textAlign="center"
        size="2xl"
        fontFamily="numerical"
        color={color || "#2d3748"}
        maxW="48rem"
        lineHeight="1.2"
        letterSpacing="tight"
        fontWeight="bold"
      >
        {title}
      </Heading>
      {success ? (
        <Stack
          spacing="6"
          mt="6"
          align="center"
          justify="center"
          textAlign="center"
        >
          {typeof successMessage === "string" ? (
            <Text>{successMessage}</Text>
          ) : (
            <RichText
              content={successMessage.raw}
              renderers={richTextRenderers}
            />
          )}

          <Button
            colorScheme={submitButton?.theme || "blue"}
            variant={submitButton?.variant || "solid"}
            onClick={() => onClose()}
          >
            Complete
          </Button>
        </Stack>
      ) : (
        <Stack
          as="form"
          spacing="6"
          mt="6"
          align="center"
          justify="center"
          onSubmit={(e) =>
            handleSubmit((values, event) =>
              onSubmit(
                { ...values, newsletter },
                setSuccess,
                setError,
                reset,
                event,
              ),
            )(e)
          }
        >
          {fields.map((field, idx) => (
            <Box key={`form-field-${idx}`} w={"full"}>
              {field.__typename === "GCMS_FormSelect" ? (
                <Fields.FormSelect
                  {...(field as FormSelectProps)}
                  {...register(field.formName as string)}
                  controlSx={{
                    error:
                      errors?.[field.formName as string] &&
                      errors?.[field.formName as string]?.message,
                  }}
                  isDisabled={slug === "parents-guide" ? true : false}
                  _disabled={{
                    color: "black",
                  }}
                  iconSize={slug === "parents-guide" ? "0" : "unset"}
                />
              ) : field.__typename === "GCMS_FormInput" ? (
                <Fields.FormInput
                  {...(field as FormInputProps)}
                  {...register(field.formName as string)}
                  controlSx={{
                    error:
                      errors?.[field.formName as string] &&
                      errors?.[field.formName as string]?.message,
                  }}
                />
              ) : field.__typename === "GCMS_FormTextarea" ? (
                <Fields.FormTextArea
                  {...(field as FormTextAreaProps)}
                  {...register(field.formName as string)}
                  controlSx={{
                    error:
                      errors?.[field.formName as string] &&
                      errors?.[field.formName as string]?.message,
                  }}
                />
              ) : (
                <></>
              )}
            </Box>
          ))}
          {from === "resource-form-modal" && (
            <Flex flexDir={"row"} align={"start"} gap={3}>
              <Checkbox onChange={() => setNewsletter(true)} mt={1} />
              <Text>{newsletterText}</Text>
            </Flex>
          )}
          <Button
            mt={4}
            variant={submitButton?.variant || "solid"}
            colorScheme={submitButton?.theme || "blue"}
            isLoading={isSubmitting}
            id={submitButton?.id}
            type="submit"
          >
            {submitButton?.label || "Submit"}
          </Button>
          {errors.submit && showError && (
            <Box textColor="red.600" textAlign="center">
              {errors?.submit?.message as React.ReactNode}
            </Box>
          )}
        </Stack>
      )}
    </Box>
  );
}
